'use client'

import Calendar from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/Calendar'
import { Box, Button, Paper, Popper } from '@mui/material'
import { useRef } from 'react'

export type HotelDateLeg = 'checkIn' | 'checkOut'

export interface HotelDateRangeFieldProps {
    checkIn: Date | null
    checkOut: Date | null
    checkInLabel: string
    checkOutLabel: string
    checkInText: string
    checkOutText: string
    isRtl: boolean
    open: boolean
    activeLeg: HotelDateLeg
    /** Check-out can only be picked once check-in exists — same rule as the mobile drawer. */
    checkOutEnabled: boolean
    calendarMinDate: Date
    onLegClick: (leg: HotelDateLeg) => void
    onCalendarClick: (dates: { from?: Date; to?: Date }) => void
}

interface LegButtonProps {
    label: string
    text: string
    active: boolean
    disabled: boolean
    onClick: () => void
}

const LegButton: React.FC<LegButtonProps> = ({ label, text, active, disabled, onClick }) => (
    <Button
        onClick={onClick}
        disabled={disabled}
        sx={{
            textTransform: 'none',
            p: 2,
            flex: 1,
            minWidth: 0,
            borderRadius: 0,
            justifyContent: 'flex-start',
            '&:hover': { backgroundColor: 'transparent' },
            fontWeight: active ? 600 : 400,
            color: active ? 'primary.main' : 'inherit',
            backgroundColor: active ? 'action.hover' : 'transparent'
        }}
    >
        <Box sx={{ textAlign: 'start' }}>
            <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'grey.600' }}>
                {label}
            </Box>
            <Box component="span" sx={{ fontSize: '0.875rem' }}>
                {text}
            </Box>
        </Box>
    </Button>
)

// Both stay dates share one calendar: picking check-in hands off to check-out in place,
// so the popover opens once for the whole range — same flow as the flights round trip.
const HotelDateRangeField: React.FC<HotelDateRangeFieldProps> = ({
    checkIn,
    checkOut,
    checkInLabel,
    checkOutLabel,
    checkInText,
    checkOutText,
    isRtl,
    open,
    activeLeg,
    checkOutEnabled,
    calendarMinDate,
    onLegClick,
    onCalendarClick
}) => {
    const anchorRef = useRef<HTMLDivElement>(null)

    return (
        <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}>
            <LegButton
                label={checkInLabel}
                text={checkInText}
                active={open && activeLeg === 'checkIn'}
                disabled={false}
                onClick={() => onLegClick('checkIn')}
            />
            <Box sx={{ borderLeft: '1px solid', borderColor: 'grey.200' }} />
            <LegButton
                label={checkOutLabel}
                text={checkOutText}
                active={open && activeLeg === 'checkOut'}
                disabled={!checkOutEnabled}
                onClick={() => onLegClick('checkOut')}
            />

            {/* Popper, not Menu: a modal backdrop would swallow clicks on the other leg's
                button, making the in-place hand-off impossible. Dismissal is owned by the
                form's ClickAwayListener, same as the destination suggestions. */}
            <Popper
                open={open && !!anchorRef.current}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                disablePortal={false}
                style={{ zIndex: 2000 }}
            >
                <Paper elevation={8} sx={{ mt: 1.5, borderRadius: 2, overflow: 'hidden' }}>
                    <Calendar
                        isRtl={isRtl}
                        className="w-100p"
                        title=""
                        selectedDates={{ from: checkIn, to: checkOut }}
                        roundTrip
                        onClickCalendar={onCalendarClick}
                        fareCalendar={{}}
                        cheapestPricesByMonth={{}}
                        directionClick={() => {}}
                        activeOnward={activeLeg === 'checkIn'}
                        activeReturn={activeLeg === 'checkOut'}
                        isMulticity={false}
                        multiCityDestinations={{}}
                        index={1}
                        minDate={calendarMinDate}
                    />
                </Paper>
            </Popper>
        </Box>
    )
}

export { HotelDateRangeField }
export default HotelDateRangeField
